// Right column — the SHA-256 hash-chained mutation ledger streaming
// as the loop's lifecycle advances. Newest-first. Each row shows the
// kind (color-coded), a short hash badge for the chain, timestamp,
// and an inline payload summary.

import { useLoopState } from "../lib/use-loop-state";
import type { LedgerEntry, LedgerKind } from "../lib/andon-loop";

export function LedgerPanel() {
  const state = useLoopState();
  const entries = [...state.ledger].reverse();

  return (
    <section className="lp">
      <header className="lp-head">
        <span className="lp-title">MUTATION LEDGER</span>
        <span className="lp-count">{state.ledger.length}</span>
      </header>
      {entries.length === 0 ? (
        <div className="lp-empty">
          ledger empty — pull the cord (guess correctly, or hit CHEAT) to see the loop run.
        </div>
      ) : (
        <ol className="lp-list">
          {entries.map((e) => (
            <LedgerRow key={e.seq} entry={e} />
          ))}
        </ol>
      )}
      <Styles />
    </section>
  );
}

const KIND_LABEL: Record<LedgerKind, string> = {
  andon_pulled:        "ANDON PULLED",
  proposed:            "PROPOSED",
  tier_verified:       "TIER VERIFIED",
  tier_rejected:       "TIER REJECTED",
  tested:              "TESTED",
  rejected_by_sandbox: "REJECTED (SANDBOX)",
  deployed:            "DEPLOYED",
};

const KIND_CLASS: Record<LedgerKind, string> = {
  andon_pulled:        "lp-row-andon",
  proposed:            "lp-row-proposed",
  tier_verified:       "lp-row-verified",
  tier_rejected:       "lp-row-rejected",
  tested:              "lp-row-tested",
  rejected_by_sandbox: "lp-row-rejected",
  deployed:            "lp-row-deployed",
};

function summary(entry: LedgerEntry): string {
  const p = entry.payload;
  switch (entry.kind) {
    case "andon_pulled":
      return `trigger=${p.trigger}`;
    case "proposed":
    case "deployed":
      return `${p.ruleName ?? "?"} { ${p.predicate ?? "?"} }`;
    case "tier_verified":
      return `proposal ${shortId(p.proposalId)}`;
    case "tier_rejected":
      return `${p.reason ?? "tier rejected"}`;
    case "tested": {
      const attempts = p.sandboxAttempts;
      const sample = p.sampleUnderProposed;
      return `sandbox ok in ${attempts} tries · sample "${sample}"`;
    }
    case "rejected_by_sandbox":
      return `${p.reason ?? "sandbox rejected"}`;
  }
}

function shortId(id: unknown): string {
  if (typeof id !== "string") return "?";
  const parts = id.split("_");
  return parts[parts.length - 1] ?? id;
}

function LedgerRow({ entry }: { entry: LedgerEntry }) {
  const time = new Date(entry.ts).toLocaleTimeString();
  return (
    <li className={`lp-row ${KIND_CLASS[entry.kind]}`}>
      <div className="lp-row-top">
        <span className="lp-row-seq">#{entry.seq}</span>
        <span className="lp-row-kind">{KIND_LABEL[entry.kind]}</span>
        <span className="lp-row-time">{time}</span>
        <span className="lp-row-hash" title={`prev: ${entry.prevHash.slice(0, 7)}`}>
          {entry.hash.slice(0, 7)}
        </span>
      </div>
      <div className="lp-row-body">{summary(entry)}</div>
    </li>
  );
}

function Styles() {
  return (
    <style>{`
      .lp {
        display: flex; flex-direction: column;
        height: 100%;
        background: var(--bg-1);
        border: 2px solid var(--border);
        border-radius: var(--radius-2);
        overflow: hidden;
      }
      .lp-head {
        display: flex; align-items: baseline; gap: var(--space-2);
        padding: var(--space-3) var(--space-4);
        border-bottom: 2px solid var(--border);
        background: var(--bg-2);
      }
      .lp-title {
        flex: 1;
        font-family: var(--font-display);
        font-weight: 800;
        font-size: 12px;
        letter-spacing: 0.1em;
      }
      .lp-count {
        font-family: var(--font-mono);
        font-size: 11px;
        background: var(--fg-0);
        color: var(--bg-0);
        padding: 2px 8px;
        border-radius: 999px;
        font-weight: 700;
      }
      .lp-empty {
        padding: var(--space-5);
        font-size: 12px;
        font-style: italic;
        color: var(--fg-2);
        text-align: center;
      }
      .lp-list {
        flex: 1;
        overflow-y: auto;
        margin: 0;
        padding: var(--space-3);
        list-style: none;
        display: flex; flex-direction: column;
        gap: var(--space-2);
      }
      .lp-row {
        background: #fff;
        border: 2px solid var(--border);
        border-left-width: 6px;
        border-radius: var(--radius-1);
        padding: var(--space-2) var(--space-3);
        font-family: var(--font-mono);
        font-size: 11px;
        animation: lp-slide 0.4s ease;
      }
      @keyframes lp-slide {
        from { transform: translateX(8px); opacity: 0; }
        to   { transform: translateX(0);   opacity: 1; }
      }
      .lp-row-top {
        display: flex; align-items: baseline; gap: 8px;
        margin-bottom: 2px;
      }
      .lp-row-seq {
        color: var(--fg-2);
        font-size: 10px;
      }
      .lp-row-kind {
        flex: 1;
        font-weight: 800;
        letter-spacing: 0.04em;
      }
      .lp-row-time {
        color: var(--fg-2);
        font-size: 10px;
      }
      .lp-row-hash {
        background: var(--bg-2);
        color: var(--fg-1);
        padding: 1px 5px;
        border-radius: 3px;
        font-size: 10px;
        font-weight: 600;
      }
      .lp-row-body {
        color: var(--fg-1);
        padding-left: 28px;
        word-break: break-word;
      }

      .lp-row-andon    { border-left-color: var(--andon-red); background: var(--andon-red-soft); }
      .lp-row-andon    .lp-row-kind { color: var(--andon-red); }
      .lp-row-proposed { border-left-color: var(--blueprint); }
      .lp-row-proposed .lp-row-kind { color: var(--blueprint); }
      .lp-row-verified { border-left-color: var(--blueprint); }
      .lp-row-verified .lp-row-kind { color: var(--blueprint); }
      .lp-row-tested   { border-left-color: var(--caution-yellow); background: var(--caution-yellow-soft); }
      .lp-row-tested   .lp-row-kind { color: #b88a00; }
      .lp-row-deployed { border-left-color: var(--safety-green); background: var(--safety-green-soft); }
      .lp-row-deployed .lp-row-kind { color: var(--safety-green); }
      .lp-row-rejected { border-left-color: var(--andon-red); background: var(--andon-red-soft); }
      .lp-row-rejected .lp-row-kind { color: var(--andon-red); }
    `}</style>
  );
}
